import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Combobox } from '@/components/molecules/Combobox';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';

interface AutoCompleteInputProps<T extends { value: string }> {
    fetchData: (input: string) => Promise<T[]> | T[];
    renderer?: (data: T) => ReactNode;
    debounceTimeInMs?: number;
    minLengthBeforeSearch?: number;
    /** The picked option, or `undefined` once the text is edited away from it. */
    onAutoCompleteSelected?: (data: T | undefined) => unknown;
    id?: string;
    placeholder?: string;
    'aria-label'?: string;
}

type SearchState<T> = {
    status: 'idle' | 'searching' | 'failed' | 'done';
    /** The text the results answer. */
    query: string;
    results: T[];
};

const NO_RESULTS: never[] = [];

/** A search field that looks options up as it is typed into, and picks one from a list. */
export function AutoCompleteInput<T extends { value: string }>({
    fetchData,
    renderer = (data) => data.value,
    debounceTimeInMs = 500,
    minLengthBeforeSearch = 1,
    onAutoCompleteSelected,
    ...props
}: AutoCompleteInputProps<T>) {
    const { t } = useTranslation();
    const [query, setQuery] = useState('');
    const [search, setSearch] = useState<SearchState<T>>({
        status: 'idle',
        query: '',
        results: [],
    });
    const debouncedQuery = useDebouncedValue(query, debounceTimeInMs);
    // Numbers each search, so an answer that arrives after a newer search started is dropped.
    const latestSearchRef = useRef(0);
    // Callers pass a new fetchData on every render; a search should start only when the settled
    // text changes, with whichever fetchData is current then.
    const fetchDataRef = useRef(fetchData);
    useEffect(() => {
        fetchDataRef.current = fetchData;
    }, [fetchData]);

    useEffect(() => {
        latestSearchRef.current += 1;
        if (debouncedQuery.length < minLengthBeforeSearch) {
            setSearch({ status: 'idle', query: debouncedQuery, results: [] });
            return;
        }
        const searchNumber = latestSearchRef.current;
        setSearch({ status: 'searching', query: debouncedQuery, results: [] });
        Promise.resolve(fetchDataRef.current(debouncedQuery)).then(
            (results) => {
                if (searchNumber === latestSearchRef.current)
                    setSearch({ status: 'done', query: debouncedQuery, results });
            },
            () => {
                if (searchNumber === latestSearchRef.current) {
                    setSearch({ status: 'failed', query: debouncedQuery, results: [] });
                }
            }
        );
    }, [debouncedQuery, minLengthBeforeSearch]);

    // An answer that lands after unmount must not set state.
    useEffect(() => {
        return () => {
            latestSearchRef.current += 1;
        };
    }, []);

    // Results answer the text they were searched with; while the field holds newer text they
    // are hidden, so Enter cannot pick a site the reader has typed away from.
    const current = search.query === query;
    const searchable = query.length >= minLengthBeforeSearch;
    const status = !searchable
        ? ''
        : !current || search.status === 'searching'
          ? t('molecules.combobox.searching')
          : search.status === 'failed'
            ? t('molecules.combobox.failed')
            : search.status === 'done' && search.results.length === 0
              ? t('molecules.combobox.noMatches')
              : '';

    return (
        <Combobox
            id={props.id}
            placeholder={props.placeholder}
            aria-label={props['aria-label']}
            query={query}
            onQueryChange={(next) => {
                setQuery(next);
                // Editing the text lets go of the option it held.
                onAutoCompleteSelected?.(undefined);
            }}
            options={current ? search.results : NO_RESULTS}
            getKey={(option) => option.value}
            renderOption={renderer}
            onSelect={(option) => {
                setQuery(option.value);
                onAutoCompleteSelected?.(option);
            }}
            status={status}
        />
    );
}
